// src/components/ErrorBoundary.js
import React from 'react';
import * as Sentry from '@sentry/react';
import { getErrorCode, markErrorCaptured, getLastEventId } from '../utils/sentry';
import { AlertTriangle, RefreshCw, Home, Copy, Check } from 'lucide-react';

// Hide the React error overlay in development
const hideReactErrorOverlay = () => {
  // Hide the iframe-based error overlay (Create React App)
  const errorOverlay = document.getElementById('webpack-dev-server-client-overlay');
  if (errorOverlay) {
    errorOverlay.style.display = 'none';
  }

  // Also try to hide the react-error-overlay container
  const reactErrorOverlay = document.querySelector('iframe[style*="error"]');
  if (reactErrorOverlay) {
    reactErrorOverlay.style.display = 'none';
  }

  // Hide any element with data-reactroot that looks like an error overlay
  const overlays = document.querySelectorAll('[id*="overlay"], [class*="overlay"]');
  overlays.forEach((el) => {
    if (el.tagName === 'IFRAME' || el.innerHTML?.includes('error')) {
      el.style.display = 'none';
    }
  });
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCode: null,
      eventId: null,
      copied: false,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Hide React's development error overlay
    hideReactErrorOverlay();
    // Try again after a short delay (overlay might appear after)
    setTimeout(hideReactErrorOverlay, 100);
    setTimeout(hideReactErrorOverlay, 500);

    // Log error to console for debugging
    console.error('Error Boundary caught an error:', error);
    console.error('Component stack:', errorInfo?.componentStack);

    // Mark error as captured to prevent duplicate from global handler
    markErrorCaptured(error);

    // Send to Sentry and get the event ID
    Sentry.withScope((scope) => {
      scope.setExtras(errorInfo);
      scope.setTag('errorBoundary', 'true');
      Sentry.captureException(error);

      // Use a small delay to ensure beforeSend has run and set the real event ID
      setTimeout(() => {
        const eventId = getLastEventId();
        const errorCode = getErrorCode(eventId);
        this.setState({ errorInfo, eventId, errorCode });
      }, 100);
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleCopyError = async () => {
    const { error, errorInfo, errorCode, eventId } = this.state;

    const errorDetails = `
Error Code: ${errorCode}
Sentry Event ID: ${eventId || 'Not available'}
Error: ${error?.toString()}
Message: ${error?.message || 'No message'}
Stack: ${error?.stack || 'No stack trace'}
Component Stack: ${errorInfo?.componentStack || 'No component stack'}
URL: ${window.location.href}
Time: ${new Date().toISOString()}
User Agent: ${navigator.userAgent}
    `.trim();

    try {
      await navigator.clipboard.writeText(errorDetails);
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    } catch (err) {
      console.error('Failed to copy error:', err);
    }
  };

  render() {
    if (this.state.hasError) {
      const { error, errorCode, eventId, copied } = this.state;

      // Extract a simplified error message
      const errorMessage = error?.message || 'An unexpected error occurred';
      const isReferenceError = errorMessage.includes('is not defined');
      const isTypeError = error?.name === 'TypeError';

      return (
        <>
          {/* CSS to hide React error overlay */}
          <style>{`
            #webpack-dev-server-client-overlay,
            iframe[style*="position: fixed"][style*="z-index"],
            body > iframe[style*="inset: 0"],
            [class*="error-overlay"],
            [id*="error-overlay"] {
              display: none !important;
              visibility: hidden !important;
              opacity: 0 !important;
              pointer-events: none !important;
            }
          `}</style>
          <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950 flex items-center justify-center p-4" style={{ position: 'relative', zIndex: 99999 }}>
            <div className="max-w-lg w-full">
            {/* Error Card */}
            <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 shadow-lg overflow-hidden">
              {/* Header */}
              <div className="bg-red-50 dark:bg-red-900/20 border-b border-red-100 dark:border-red-900/30 p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-100 dark:bg-red-900/40 rounded-lg">
                    <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
                  </div>
                  <div>
                    <h1 className="text-lg font-semibold text-red-800 dark:text-red-200">
                      Something went wrong
                    </h1>
                    <p className="text-sm text-red-600 dark:text-red-400">
                      The application encountered an unexpected error
                    </p>
                  </div>
                </div>
              </div>

              {/* Body */}
              <div className="p-4 space-y-4">
                {/* Error Code */}
                <div className="flex items-center justify-between bg-zinc-100 dark:bg-zinc-800 rounded-lg px-3 py-2">
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                      Error Code
                    </p>
                    <p className="text-sm font-mono font-medium text-zinc-800 dark:text-zinc-200">
                      {errorCode}
                    </p>
                  </div>
                  <button
                    onClick={this.handleCopyError}
                    className="p-2 hover:bg-zinc-200 dark:hover:bg-zinc-700 rounded-lg transition-colors"
                    title="Copy error details"
                  >
                    {copied ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : (
                      <Copy className="w-4 h-4 text-zinc-500" />
                    )}
                  </button>
                </div>

                {/* Simplified Error Message */}
                <div className="bg-zinc-50 dark:bg-zinc-800/50 rounded-lg p-3">
                  <p className="text-[10px] uppercase tracking-wide text-zinc-500 dark:text-zinc-400 mb-1">
                    Error Details
                  </p>
                  <p className="text-sm text-zinc-700 dark:text-zinc-300 font-mono break-all">
                    {errorMessage.length > 150
                      ? errorMessage.substring(0, 150) + '...'
                      : errorMessage}
                  </p>
                </div>

                {/* Helpful Tips */}
                <div className="text-sm text-zinc-600 dark:text-zinc-400 space-y-2">
                  <p>Try the following:</p>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                    <li>Refresh the page to try again</li>
                    <li>Go back to the home page</li>
                    <li>Clear your browser cache and cookies</li>
                    {isReferenceError && (
                      <li className="text-amber-600 dark:text-amber-400">
                        This may be a temporary issue. If it persists, contact support.
                      </li>
                    )}
                  </ul>
                </div>

                {/* Support Info */}
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-900/30 rounded-lg p-3">
                  <p className="text-xs text-blue-700 dark:text-blue-300">
                    If this problem continues, please contact support with the error code above.
                    Click the copy button to copy full error details.
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="border-t border-zinc-200 dark:border-zinc-800 p-4 flex gap-2">
                <button
                  onClick={this.handleReload}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2
                           bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900
                           rounded-lg text-sm font-medium hover:bg-zinc-800 dark:hover:bg-zinc-200
                           transition-colors"
                >
                  <RefreshCw className="w-4 h-4" />
                  Refresh Page
                </button>
                <button
                  onClick={this.handleGoHome}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2
                           border border-zinc-300 dark:border-zinc-700
                           text-zinc-700 dark:text-zinc-300
                           rounded-lg text-sm font-medium hover:bg-zinc-100 dark:hover:bg-zinc-800
                           transition-colors"
                >
                  <Home className="w-4 h-4" />
                  Go Home
                </button>
              </div>
            </div>

            {/* Event ID for support */}
            {eventId && (
              <p className="text-center text-[10px] text-zinc-400 dark:text-zinc-600 mt-3 font-mono break-all px-4">
                Event ID: {eventId}
              </p>
            )}

            {/* Footer */}
            <p className="text-center text-xs text-zinc-400 dark:text-zinc-600 mt-2">
              Nexus Inventory Management
            </p>
          </div>
        </div>
        </>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
